import React, { useState, useEffect } from 'react';

const UserManagement = ({ apiEndpoints }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionInProgress, setActionInProgress] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);

  console.log('UserManagement received apiEndpoints:', apiEndpoints);

  // Validate that required API endpoints are available
  const hasRequiredEndpoints = apiEndpoints && 
    typeof apiEndpoints === 'object' && 
    apiEndpoints.users && 
    apiEndpoints.toggleAdmin;

  // Add a function to fetch API debug info
  const fetchApiDebugInfo = async () => {
    try {
      console.log('Fetching API debug info');
      const response = await fetch('/api_debug', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('API debug info:', data);
      setDebugInfo(data);
      
      // Try using the API paths from the debug info
      if (data.api_endpoints && data.api_endpoints.users) {
        fetchUsersWithPath(data.api_endpoints.users);
      }
    } catch (err) {
      console.error('Error fetching API debug info:', err);
    }
  };

  // Add a direct fetch button for testing
  const directFetch = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Attempting direct fetch to /admin/users');
      const response = await fetch('/admin/users', {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Direct fetch response:', data);
      setUsers(data);
      setLoading(false);
    } catch (err) {
      console.error('Direct fetch error:', err);
      setError(`Direct fetch error: ${err.message}`);
      setLoading(false);
      
      // If direct fetch fails, try getting API debug info
      fetchApiDebugInfo();
    }
  };

  // Add a button to directly attempt the fetch using debug information
  const forceDirectFetch = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
      if (!csrfToken) {
        console.warn('No CSRF token found, proceeding without it');
      }
      
      // First get the debug info to find the right path
      const debugResponse = await fetch('/api_debug', {
        headers: {
          'Accept': 'application/json',
        },
        credentials: 'same-origin'
      });
      
      if (!debugResponse.ok) {
        throw new Error(`Error getting debug info: ${debugResponse.status}`);
      }
      
      const debugData = await debugResponse.json();
      console.log('Debug data for force fetch:', debugData);
      
      if (!debugData.api_endpoints || !debugData.api_endpoints.users) {
        throw new Error('Could not find users endpoint in debug data');
      }
      
      // Now try the actual users fetch
      const response = await fetch(debugData.api_endpoints.users, {
        headers: {
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken || '',
        },
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error(`Direct user fetch failed: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Force direct fetch response:', data);
      setUsers(data);
    } catch (err) {
      console.error('Force direct fetch error:', err);
      setError(`Force fetch error: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Add a function to fetch users with a specific path
  const fetchUsersWithPath = async (path) => {
    try {
      setLoading(true);
      setError(null);
      console.log(`Attempting fetch with path: ${path}`);
      
      const response = await fetch(path, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Fetch response with path:', data);
      setUsers(data);
      setLoading(false);
    } catch (err) {
      console.error(`Error fetching with path ${path}:`, err);
      setError(`Error fetching with path ${path}: ${err.message}`);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (hasRequiredEndpoints) {
      fetchUsers();
    } else {
      setLoading(false);
      setError("API endpoints configuration is missing. Cannot fetch user data.");
      
      // If apiEndpoints are missing, try getting API debug info
      fetchApiDebugInfo();
    }
  }, [hasRequiredEndpoints]);

  const fetchUsers = async () => {
    setLoading(true);
    setError(null);
    
    try {
      if (!apiEndpoints.users) {
        throw new Error("Users API endpoint is not configured");
      }
      
      console.log(`Fetching users from: ${apiEndpoints.users}`);
      const response = await fetch(apiEndpoints.users, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        credentials: 'same-origin'
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching users: ${response.status} ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Fetch users response:', data);
      setUsers(data);
    } catch (err) {
      console.error("Error fetching users:", err);
      setError(err.message || "Failed to fetch users");
      
      // If fetch users fails, try direct fetch
      directFetch();
    } finally {
      setLoading(false);
    }
  };

  const toggleAdmin = async (userId) => {
    if (actionInProgress || !apiEndpoints.toggleAdmin) return;
    
    setActionInProgress(true);
    try {
      const endpoint = apiEndpoints.toggleAdmin.replace(':id', userId);
      const csrfToken = document.querySelector('meta[name="csrf-token"]');
      
      if (!csrfToken) {
        throw new Error("CSRF token not found. Please refresh the page.");
      }
      
      const response = await fetch(endpoint, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken.content
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Failed to toggle admin status (${response.status})`);
      }
      
      const data = await response.json();
      if (data.success) {
        // Update the user in the local state
        setUsers(users.map(user => 
          user.id === userId ? { ...user, admin: !user.admin } : user
        ));
      }
    } catch (err) {
      console.error("Error toggling admin status:", err);
      setError(err.message || "Failed to update user");
    } finally {
      setActionInProgress(false);
    }
  };

  if (!hasRequiredEndpoints) {
    return (
      <div className="alert alert-danger">
        <h4 className="alert-heading">Configuration Error</h4>
        <p>
          The API endpoints required for user management are missing or invalid.
        </p>
        <hr />
        <p className="mb-0">
          Please ensure the application is properly configured with valid API endpoints.
        </p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <div className="spinner-border text-primary me-3" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <div>Loading users...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger">
        <h4 className="alert-heading">Error</h4>
        <p>{error}</p>
        <hr />
        <button 
          className="btn btn-outline-danger" 
          onClick={fetchUsers}
          disabled={actionInProgress || !hasRequiredEndpoints}
        >
          <i className="fas fa-sync-alt me-2"></i> Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>User Management</h3>
        <div>
          <button 
            className="btn btn-primary" 
            onClick={fetchUsers}
            disabled={actionInProgress || !hasRequiredEndpoints}
          >
            <i className="fas fa-sync-alt me-2"></i> Refresh
          </button>
        </div>
      </div>
      
      <div className="table-responsive">
        <table className="table table-hover">
          <thead className="table-light">
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Created At</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td colSpan="5" className="text-center py-4">
                  <i className="fas fa-users text-muted mb-3" style={{ fontSize: '2rem' }}></i>
                  <p className="mb-0">No users found</p>
                </td>
              </tr>
            ) : (
              users.map(user => (
                <tr key={user.id}>
                  <td>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`badge ${user.admin ? 'bg-primary' : 'bg-secondary'}`}>
                      {user.admin ? 'Admin' : 'User'}
                    </span>
                  </td>
                  <td>{new Date(user.created_at).toLocaleDateString()}</td>
                  <td>
                    <button
                      className={`btn btn-sm ${user.admin ? 'btn-warning' : 'btn-success'}`}
                      onClick={() => toggleAdmin(user.id)}
                      disabled={actionInProgress}
                    >
                      {user.admin ? (
                        <>
                          <i className="fas fa-user-minus me-1"></i> Remove Admin
                        </>
                      ) : (
                        <>
                          <i className="fas fa-user-plus me-1"></i> Make Admin
                        </>
                      )}
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UserManagement; 
